/**
 * The signed-out screen (§3.1): two equal columns, the form on the left and
 * what the account holds on the right.
 *
 * Sign in and sign up are one component because they are one form with a
 * different verb — and because the toggle between them has to keep what has
 * already been typed.
 *
 * Signing up asks one question a sign-in does not: which kind of account this
 * is. It is asked here rather than after, because the answer decides what the
 * app looks like from the first screen — a personal account is never shown an
 * organization, and an organization needs a name before it can hold seats.
 *
 * An invitation (`?invite=…`) is the third way in. It is read before anything
 * is typed, so the screen can name the organization that is asking, fix the
 * address it was sent to, and skip the question entirely: somebody joining a
 * team is not choosing between the two kinds.
 */
import { useEffect, useState } from 'react';
import type React from 'react';
import { color, font, radius } from '../theme/tokens';
import { AirlockLockup } from '../components/AirlockMark';
import { Field, Input, PrimaryButton } from '../components/primitives';
import { gerund } from '../lib/format';
import { LinkNotice } from './LinkMachine';
import { TwoFactorStep } from './identity/TwoFactorStep';
import { AuthStatePage, type AuthState } from './auth-state';
import { AccountKind, FixedField } from './auth-fields';
import { api, ApiError, API_BASE, apiPath, apiQuery } from '../lib/api';
import { ssoParam, tokenParam } from '../lib/api-path';
import type { AccountType, InvitePreview, Me } from '../lib/types';

/**
 * Why a GitHub sign-in came back without a session. The Worker names the reason
 * in the query rather than in a body, because the return from GitHub is a
 * navigation and not a fetch.
 */
const GITHUB_FAILURES: Record<string, string> = {
  cancelled: 'That GitHub sign-in was cancelled.',
  state: 'That sign-in link had expired. Try again.',
  unverified: 'Verify an email address on GitHub, then sign in again.',
  closed: 'This account is closed. Its history stays with the organizations it worked in, and it can no longer sign in.',
  failed: 'We could not finish that GitHub sign-in. Try an email address and password.',
  sso: 'Your organization signs in with single sign-on. Use Continue with SSO.',
  'sso-none': 'No organization signs in with single sign-on at that address. Use your password or GitHub.',
  'sso-failed': 'Your identity provider did not complete that sign-in. Try again, or ask your administrator.',
  'sso-domain': 'Single sign-on signs in only addresses at your organization’s verified domains.',
  'sso-confirm': 'Continue to sign in with your organization’s identity provider.'
};

/**
 * A single sign-on start that arrived from somewhere else (`?auth=sso-confirm&sso=…`).
 *
 * The Worker no longer sends a navigation it did not see this page make
 * straight on to an identity provider: a link to `/api/auth/sso/start?org=…`
 * would otherwise turn this domain into a redirect to whatever sign-on URL an
 * organization configured. It lands here instead, with the address or slug
 * typed in, and the person's own click on Continue is what goes on. Read before
 * `githubFailure` takes the query away; a value in any other shape is dropped.
 */
function arrivedForSso(): string | null {
  if (new URLSearchParams(window.location.search).get('auth') !== 'sso-confirm') return null;
  return ssoParam(window.location.search);
}

/**
 * A sign-in that came back owing a second factor (`?auth=2fa`, `?auth=2fa-enrol`,
 * from GitHub's return): read before `githubFailure` takes the query away.
 */
function arrivedOwingFactor(): 'verify' | 'enrol' | null {
  const reason = new URLSearchParams(window.location.search).get('auth');
  return reason === '2fa' ? 'verify' : reason === '2fa-enrol' ? 'enrol' : null;
}

/**
 * The tone of each return (§37.7): neutral when nothing failed on the person's
 * side — a cancel, an expired link, the steer towards SSO — and an error when
 * the sign-in was refused. Never teal.
 */
const NEUTRAL_RETURNS = new Set(['cancelled', 'state', 'sso', 'sso-confirm']);

function githubFailure(): { text: string; tone: 'neutral' | 'bad' } | null {
  const reason = new URLSearchParams(window.location.search).get('auth');
  if (!reason) return null;
  if (reason === '2fa' || reason === '2fa-enrol') {
    window.history.replaceState(null, '', window.location.pathname);
    return null;
  }
  // Dropped from the address bar so a reload does not show it again.
  window.history.replaceState(null, '', window.location.pathname);
  const known = reason in GITHUB_FAILURES;
  return {
    text: GITHUB_FAILURES[known ? reason : 'failed'],
    tone: known && NEUTRAL_RETURNS.has(reason) ? 'neutral' : 'bad'
  };
}

/**
 * The token from a reset mail, read once and taken out of the address bar — it
 * is a credential, and leaving it there puts it in the history of a browser
 * somebody may share.
 */
function resetToken(): string | null {
  const token = tokenParam(window.location.search, 'reset');
  if (!token) return null;
  window.history.replaceState(null, '', window.location.pathname);
  return token;
}

export function Auth({
  initialMode,
  initialKind = 'personal',
  invite,
  device,
  siteUrl,
  docsUrl,
  githubSignIn,
  notice: arrivedWith = null,
  onSignedIn
}: {
  initialMode: 'signin' | 'signup';
  /**
   * Which of the two kinds the sign-up question opens on. A starting position,
   * not a decision — whoever arrives from the site's Team card means to make an
   * organization, and should not have to say so twice.
   */
  initialKind?: AccountType;
  /** The token from an invitation link, when this browser arrived with one. */
  invite?: string | null;
  /**
   * The code of a machine waiting to be approved, when a desktop app sent the
   * browser here. Signing in is a step on the way to answering it, so the
   * screen says so rather than leaving somebody wondering why they were asked.
   */
  device?: string | null;
  siteUrl: string;
  /** The manual, for the site header's Docs link. */
  docsUrl: string;
  githubSignIn: boolean;
  /**
   * Why this screen is on show, where the shell knows — a session that idled
   * out, or one that ended somewhere else. Read as this screen mounts, which is
   * exactly when the shell has just decided to show it.
   */
  notice?: string | null;
  onSignedIn: (me: Me) => void;
}): React.JSX.Element {
  // `forgot` asks for the link; `reset` is the screen the link opens on.
  const [token] = useState(resetToken);
  // Declared before `arrival`, whose initializer clears the query this reads.
  const [ssoArrival] = useState(arrivedForSso);
  const [mode, setMode] = useState<'signin' | 'signup' | 'forgot' | 'reset' | 'sso'>(
    token ? 'reset' : ssoArrival ? 'sso' : initialMode
  );
  const [email, setEmail] = useState(ssoArrival ?? '');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [accountType, setAccountType] = useState<AccountType>(initialKind);
  const [organizationName, setOrganizationName] = useState('');
  const [busy, setBusy] = useState(false);
  // Declared before `error`, whose initializer clears the query this reads.
  const [factor, setFactor] = useState<'verify' | 'enrol' | null>(arrivedOwingFactor);
  const [arrival] = useState(githubFailure);
  const [notice, setNotice] = useState<string | null>(arrivedWith ?? (arrival?.tone === 'neutral' ? arrival.text : null));
  const [error, setError] = useState<string | null>(null);
  // A refused return from GitHub or SSO: an error note at the head of the form (§10.6), not under Password.
  const [headError, setHeadError] = useState<string | null>(arrival?.tone === 'bad' ? arrival.text : null);
  const [invitation, setInvitation] = useState<InvitePreview | null>(null);
  // The link is sent, or the reset link was refused: a state page instead of the form (§37.6).
  const [state, setState] = useState<AuthState | null>(null);

  // A token that names nothing is simply not an invitation: the screen falls
  // back to an ordinary sign-up rather than announcing a failure nobody can act
  // on. The address is fixed to the one invited, because a seat belongs to that
  // address and signing up as another would leave the invitation unclaimed.
  useEffect(() => {
    if (!invite) return;
    void api
      .get<{ invite: InvitePreview }>(apiPath`/invites/${invite}`)
      .then((answer) => {
        setInvitation(answer.invite);
        setEmail(answer.invite.email);
      })
      .catch(() => setInvitation(null));
  }, [invite]);

  const signin = mode === 'signin';
  // The submit's verb, and its -ing form while it runs (rule 6).
  const submitLabel =
    mode === 'sso'
      ? 'Continue'
      : mode === 'forgot'
        ? 'Send the link'
        : mode === 'reset'
          ? 'Set the password'
          : signin
            ? 'Sign in'
            : 'Create account';

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setHeadError(null);
    if (mode === 'sso') {
      // A navigation, not a fetch: the browser leaves for the identity provider
      // and comes back signed in. An address finds the organization by its
      // verified domain; anything else is read as the organization's slug.
      const query = new URLSearchParams(email.includes('@') ? { email } : { org: email });
      if (device) query.set('device', device);
      window.location.assign(`${API_BASE}/auth/sso/start?${apiQuery(query)}`);
      return;
    }
    try {
      if (mode === 'forgot') {
        // The answer never says whether the address has an account: it is the
        // same either way, deliberately (`forgotPassword` in the Worker).
        await api.post<{ ok: boolean }>('/auth/forgot', { email });
        setState({ kind: 'sent', email });
        return;
      }
      if (mode === 'reset') {
        await api.post<{ ok: boolean; email: string }>('/auth/reset', { token, password });
        setNotice('Your password has been set, and every other browser has been signed out. Sign in with it.');
        setPassword('');
        setMode('signin');
        return;
      }
      const me = signin
        ? await api.post<Me | { twoFactor: 'verify' | 'enrol' }>('/auth/signin', { email, password })
        : await api.post<Me>('/auth/signup', {
            email,
            password,
            name,
            // An invitee joins somebody else's organization: the kind is not
            // theirs to choose, and the seat — claimed on the address as the
            // account is created — is what makes it an organization account.
            accountType: invitation ? 'personal' : accountType,
            organizationName: !invitation && accountType === 'org' ? organizationName : undefined
          });
      // A second factor is owed: no session yet, the next screen asks for it.
      if ('twoFactor' in me) {
        setFactor(me.twoFactor);
        return;
      }
      onSignedIn(me);
    } catch (failure) {
      // A reset link the Worker no longer knows (used, or past its hour) ends the form.
      if (mode === 'reset' && failure instanceof ApiError && failure.status === 404) {
        setState({ kind: 'invalid' });
        return;
      }
      setError(failure instanceof ApiError ? failure.message : 'That did not work. Try again.');
      // Refused because the address signs in through its organization: the
      // next screen is the one that works.
      if (failure instanceof ApiError && failure.status === 403 && /single sign-on/.test(failure.message)) setMode('sso');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="al-auth" style={{ minHeight: '100vh', fontFamily: font.sans, color: color.ink, background: color.surface }}>
      <SiteHeader siteUrl={siteUrl} docsUrl={docsUrl} />
      {/* One column of 360 under the site's own header, no box around the
          form: the sign-in page is the site's next page (redesign §37.1). */}
      <div className="al-auth-column" style={{ maxWidth: 360, margin: '0 auto', padding: '72px 0 64px' }}>
        {factor ? (
          // A sign-in owing its second factor: this screen and nothing else,
          // outside the sign-in form (it holds a form of its own).
          <div style={{ width: '100%' }}>
            <h1 className="al-auth-title" style={TITLE}>
              {factor === 'enrol' ? 'Set up two-factor' : 'Two-factor'}
            </h1>
            <p style={{ fontSize: 14.5, color: color.inkSubtle, margin: '0 0 28px' }}>
              {factor === 'enrol' ? 'One more step before your account opens.' : 'Type the code your authenticator app shows.'}
            </p>
            <TwoFactorStep purpose={factor} onSignedIn={onSignedIn} />
          </div>
        ) : state ? (
          <AuthStatePage
            state={state}
            siteUrl={siteUrl}
            onBack={() => {
              setState(null);
              setMode('signin');
            }}
            onNewLink={() => {
              setState(null);
              setMode('forgot');
            }}
          />
        ) : (
        <form onSubmit={(event) => void submit(event)} style={{ width: '100%' }}>
          <h1 className="al-auth-title" style={TITLE}>
            {mode === 'sso'
              ? 'Continue with SSO'
              : mode === 'forgot'
              ? 'Reset your password'
              : mode === 'reset'
                ? 'Set a new password'
                : signin
                  ? 'Sign in'
                  : invitation
                    ? `Join ${invitation.organization}`
                    : 'Create your account'}
          </h1>
          <p style={{ fontSize: 14.5, color: color.inkSubtle, margin: '0 0 28px' }}>
            {mode === 'sso'
              ? 'Type your work email, or your organization’s short name. Your identity provider signs you in.'
              : mode === 'forgot'
              ? 'Type the address on the account and we will send a link. It is good for one hour.'
              : mode === 'reset'
                ? 'Choose a new password. Every browser signed in to this account will be signed out.'
                : signin
                  ? 'Your key, your invoices and your downloads.'
                  : invitation
                    ? `${invitation.invitedBy ?? 'Somebody'} invited ${invitation.email} to their team. Set a password to join it — a seat is given to you there.`
                    : 'One account holds your licence, your builds and — if you want one — your team.'}
          </p>

          {notice && (
            <p role="status" style={{ ...NOTE, background: color.offBg, color: color.offText }}>
              {notice}
            </p>
          )}
          {headError && (
            <p role="alert" style={{ ...NOTE, background: color.badBg, color: color.badText }}>
              {headError}
            </p>
          )}

          {device && <LinkNotice code={device} />}

          {/* A link and not a fetch: the browser has to *leave* for github.com
              and come back, and the Worker sets the session on the way in. A
              deployment with no OAuth app offers no button rather than one that
              cannot work — `githubSignIn` is what the Worker says about itself. */}
          {githubSignIn && mode !== 'forgot' && mode !== 'reset' && mode !== 'sso' && (
            <>
              <a
                href={`${API_BASE}/auth/github/start`}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  fontFamily: 'inherit',
                  fontSize: 14,
                  fontWeight: 500,
                  textDecoration: 'none',
                  // A secondary pill, full width: the ink one is the form's own verb (§16).
                  color: color.ink,
                  background: color.surface,
                  border: `1px solid ${color.lineStrong}`,
                  height: 36,
                  borderRadius: radius.chip,
                  cursor: 'pointer'
                }}
              >
                <span style={{ fontFamily: font.mono, fontSize: 13 }}>GH</span> Continue with GitHub
              </a>

              <div
                style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '22px 0', color: color.inkGhost, fontSize: 12 }}
              >
                <span style={{ flex: 1, height: 1, background: color.lineStrong, display: 'block' }} />
                or
                <span style={{ flex: 1, height: 1, background: color.lineStrong, display: 'block' }} />
              </div>
            </>
          )}

          {/* The choice, and only where it is a choice: signing in does not ask,
              and an invitation has already answered. */}
          {!signin && mode === 'signup' && !invitation && (
            <AccountKind
              value={accountType}
              onChange={setAccountType}
              organizationName={organizationName}
              onOrganizationName={setOrganizationName}
              inputStyle={{ fontSize: 14.5, padding: '12px 13px', background: color.surface }}
            />
          )}

          {mode === 'signup' && (
            <>
              <Field label="Name" htmlFor="auth-name">
                <Input
                  id="auth-name"
                  autoComplete="name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Thomas Lavoie"
                  style={{ fontSize: 14.5, padding: '12px 13px', background: color.surface }}
                />
              </Field>
              <div style={{ height: 16 }} />
            </>
          )}

          {invitation && mode === 'signup' ? (
            <>
              <FixedField id="auth-email" label="Email" value={invitation.email} help="The invitation was sent to this address." />
              <div style={{ height: 16 }} />
            </>
          ) : mode !== 'reset' && (
            <>
              <Field label={mode === 'sso' ? 'Work email or organization' : 'Email'} htmlFor="auth-email">
            <Input
              id="auth-email"
              type={mode === 'sso' ? 'text' : 'email'}
              required
              autoComplete="email"
              value={email}
              // Fixed to the address the invitation was sent to: the seat is
              // held for that address, and any other one would sign up into an
              // account the invitation cannot reach.
              readOnly={Boolean(invitation) && !signin}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
                  style={{ fontSize: 14.5, padding: '12px 13px', background: color.surface }}
                />
              </Field>
              {mode !== 'forgot' && mode !== 'sso' && <div style={{ height: 16 }} />}
            </>
          )}
          {mode !== 'forgot' && mode !== 'sso' && (
            <Field label="Password" htmlFor="auth-password">
              <Input
                id="auth-password"
                type="password"
                required
                minLength={signin ? undefined : 12}
                autoComplete={signin ? 'current-password' : 'new-password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder={signin ? '••••••••••' : 'at least 12 characters'}
                style={{ fontSize: 14.5, padding: '12px 13px', background: color.surface }}
              />
            </Field>
          )}

          {error && (
            <p
              role="alert"
              style={{
                fontSize: 13.5,
                color: color.badText,
                background: color.badSoft,
                border: `1px solid ${color.badLine}`,
                borderRadius: radius.md,
                padding: '10px 12px',
                margin: '16px 0 0'
              }}
            >
              {error}
            </p>
          )}

          <div style={{ height: 20 }} />
          <PrimaryButton full size="lg" type="submit">
            {busy ? gerund(submitLabel) : submitLabel}
          </PrimaryButton>

          {signin && (
            <button
              type="button"
              onClick={() => {
                setMode('sso');
                setError(null);
                setNotice(null);
                setHeadError(null);
              }}
              style={{
                display: 'block',
                width: '100%',
                marginTop: 10,
                background: 'none',
                border: `1px solid ${color.lineStrong}`,
                borderRadius: radius.chip,
                height: 36,
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 14,
                color: color.ink
              }}
            >
              Continue with SSO
            </button>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 18, fontSize: 13.5 }}>
            <button
              type="button"
              onClick={() => {
                // From either of the two password screens the way back is
                // sign-in; otherwise this toggles the pair as it always has.
                setMode(mode === 'forgot' || mode === 'reset' || mode === 'sso' ? 'signin' : signin ? 'signup' : 'signin');
                setError(null);
                setNotice(null);
                setHeadError(null);
              }}
              style={{
                background: 'none',
                border: 0,
                padding: 0,
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 13.5,
                color: color.accentText
              }}
            >
              {mode === 'forgot' || mode === 'reset' || mode === 'sso'
                ? 'Back to sign in'
                : signin
                  ? 'No account yet? Create one'
                  : 'Already have an account? Sign in'}
            </button>
            {/* A real flow, not a mailto to support: asking a human to reset a
                password makes convincing that human the way into an account. */}
            <button
              type="button"
              hidden={mode === 'forgot' || mode === 'reset' || mode === 'sso'}
              onClick={() => {
                setMode('forgot');
                setError(null);
                setNotice(null);
                setHeadError(null);
              }}
              style={{
                background: 'none',
                border: 0,
                padding: 0,
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 13.5,
                color: color.accentText
              }}
            >
              Forgot password?
            </button>
          </div>

          <p style={{ fontSize: 12.5, color: color.inkFaint, lineHeight: 1.5, margin: '16px 0 0' }}>
            By continuing you accept the <a href={`${siteUrl.replace(/\/+$/, '')}/legal/cgv`}>Terms of sale &amp; use</a> and the{' '}
            <a href={`${siteUrl.replace(/\/+$/, '')}/legal/privacy`}>privacy policy</a>.
          </p>
        </form>
        )}

        {/* What the dark aside used to say, under the form on a hairline. */}
        <div style={{ marginTop: 56, paddingTop: 28, borderTop: `1px solid ${color.line}` }}>
          <p style={{ fontSize: 15, fontWeight: 600, color: color.ink, margin: 0 }}>
            Your key, your invoices, your organization — in one place.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
            {[
              'Retrieve your perpetual licence key at any time',
              'Download Airlock for macOS',
              'Manage billing, invoices and VAT details',
              'Or start an organization, and invite your team to seats'
            ].map((line) => (
              <div key={line} style={{ display: 'flex', gap: 8, fontSize: 13.5, color: color.inkBody }}>
                <span aria-hidden="true" style={{ color: color.accentText }}>
                  ✓
                </span>
                {line}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** The form's note (§10.6): radius 8, 10 × 12, the tone's ground and text, no border, no icon. */
const NOTE: React.CSSProperties = { fontSize: 13, lineHeight: 1.5, borderRadius: 8, padding: '10px 12px', margin: '0 0 20px' };

const TITLE: React.CSSProperties = { fontSize: 48, lineHeight: 1.04, letterSpacing: '-.03em', fontWeight: 700, margin: '0 0 10px' };

/**
 * The site's own header (redesign §37.1): the mark home, the site's four pages,
 * Download on the right. No « Sign in » pill — this is the sign-in page.
 */
function SiteHeader({ siteUrl, docsUrl }: { siteUrl: string; docsUrl: string }): React.JSX.Element {
  const site = siteUrl.replace(/\/+$/, '');
  const link: React.CSSProperties = { fontSize: 13.5, color: color.ink, textDecoration: 'none' };
  return (
    <header
      className="al-site-header"
      style={{ height: 52, display: 'flex', alignItems: 'center', gap: 28, padding: '0 40px', borderBottom: `1px solid ${color.line}` }}
    >
      <a href={site || '/'} style={{ textDecoration: 'none', color: color.ink, display: 'flex' }} aria-label="Airlock home">
        <AirlockLockup labelSize={12} />
      </a>
      <nav className="al-site-nav" aria-label="Site" style={{ display: 'flex', gap: 28 }}>
        <a href={`${site}/`} style={link}>
          Home
        </a>
        <a href={`${site}/pricing`} style={link}>
          Pricing
        </a>
        <a href={`${site}/changelog`} style={link}>
          Changelog
        </a>
        <a href={docsUrl} style={link}>
          Docs
        </a>
      </nav>
      <span style={{ flex: 1 }} />
      {/* Signed out there is no key to download with: the site's home offers the build. */}
      <a href={`${site}/`} style={{ ...link, fontWeight: 500 }}>
        Download
      </a>
    </header>
  );
}
